import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { ecoEfficiencyDashboardService } from "../service/eco-efficiency-dashboard.service";
import {
  EcoEfficiencyDashboardMonthlyResults,
  EcoEfficiencyDashboardYearlyResults,
  EcoEfficiencyV2,
} from "../models/dto/response/ResponseEcoEfficiencyV2";
import { EconomicIndicator } from "../models/dto/response/ResponseCreateEconomicIndicators";
import { EcoEfficiencyService } from "../service/eco-efficiency.service";
import { SubOrganizations } from "../models/SubOrganization";
import { useAuth } from "./UserContext";
import { STORAGE_KEYS } from "../constants";

const BASE_STORAGE_KEY = STORAGE_KEYS.ECO_EFF_FILTER_PREFIX;

export interface EcoEfficiencyDashboardFilter {
  organizationType: "organization" | "sub-organization";
  organization: string;
  subOrganizations: string[];
  indicator: string;
  period: string;
  baseYear: number;
}

type FetchedEcoEffData = {
  monthlyData: EcoEfficiencyDashboardMonthlyResults;
  yearlyData: EcoEfficiencyDashboardYearlyResults;
};

type EcoEfficiencyDashboardState = {
  ecoEfficiencyDashboardFilterForm: UseFormReturn<
    EcoEfficiencyDashboardFilter,
    any,
    EcoEfficiencyDashboardFilter
  >;
  ecoEfficiency: EcoEfficiencyV2[];
  fetchEcoEffData: (
    indicatorId: string,
    startDate: string,
    endDate: string,
  ) => Promise<void>;
  selectedEcoEfficiency?: EcoEfficiencyV2;
  setSelectedEcoEfficiency: (item: EcoEfficiencyV2 | undefined) => void;
  fetchedEcoEffData?: FetchedEcoEffData;
  indicators: EconomicIndicator[];
  yearly: EcoEfficiencyDashboardYearlyResults | undefined;
  monthly: EcoEfficiencyDashboardMonthlyResults | undefined;
  subOrganizations: SubOrganizations[];
  setSubOrganizations: Dispatch<SetStateAction<SubOrganizations[]>>;
};

export const EcoEfficiencyDashboardContext = (): EcoEfficiencyDashboardState =>
  useContext(Context);

export const Context = createContext<EcoEfficiencyDashboardState>(
  {} as EcoEfficiencyDashboardState,
);

export const EcoEfficiencyDashboardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { organization: selectedOrganization } = useAuth();

  const [ecoEfficiency, setEcoEfficiency] = useState<EcoEfficiencyV2[]>([]);
  const [selectedEcoEfficiency, setSelectedEcoEfficiency] =
    useState<EcoEfficiencyV2>();
  const [fetchedEcoEffData, setFetchedEcoEffData] =
    useState<FetchedEcoEffData>();
  const [indicators, setIndicators] = useState<EconomicIndicator[]>([]);
  const [yearly, setYearly] = useState<EcoEfficiencyDashboardYearlyResults>();
  const [monthly, setMonthly] =
    useState<EcoEfficiencyDashboardMonthlyResults>();
  const [subOrganizations, setSubOrganizations] = useState<SubOrganizations[]>(
    [],
  );

  const defaultFilterValues: EcoEfficiencyDashboardFilter = {
    organizationType: "sub-organization",
    organization: "",
    subOrganizations: [],
    indicator: "",
    period: "",
    baseYear: 0,
  };

  const ecoEfficiencyDashboardFilterForm =
    useForm<EcoEfficiencyDashboardFilter>({
      defaultValues: defaultFilterValues,
    });

  const watchedValues = ecoEfficiencyDashboardFilterForm.watch();
  const selectSubOrganizations = watchedValues.subOrganizations;
  const selectedPeriodId = watchedValues.period;

  const getStorageKey = (orgId: string) => `${BASE_STORAGE_KEY}_${orgId}`;

  const loadedOrgRef = useRef<number | null>(selectedOrganization?.id ?? null);

  // 1. LOAD Effect: โหลดค่าเมื่อ Organization ID เปลี่ยน
  useEffect(() => {
    if (selectedOrganization?.id) {
      const orgKey = getStorageKey(`${selectedOrganization.id}`);

      // ✅ โหลดเสร็จแล้ว อัปเดต Ref ให้ตรงกับ Org ปัจจุบัน
      loadedOrgRef.current = selectedOrganization.id;

      try {
        const saved = localStorage.getItem(orgKey);
        if (saved) {
          const parsed = JSON.parse(saved);

          // ถ้าเป็น organization ให้บังคับ subOrganizations เป็น id ของมันเลย
          if (parsed.organizationType === "organization") {
            parsed.subOrganizations = [String(selectedOrganization.id)];
          }

          ecoEfficiencyDashboardFilterForm.reset(parsed);
        } else {
          // ถ้าไม่มีค่าใน Storage ก็ใช้ default
          const initValues = { ...defaultFilterValues };
          if (initValues.organizationType === "organization") {
            initValues.subOrganizations = [String(selectedOrganization.id)];
          }
          ecoEfficiencyDashboardFilterForm.reset(initValues);
        }
      } catch (e) {
        ecoEfficiencyDashboardFilterForm.reset(defaultFilterValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.id, selectedOrganization?.name]);

  // 2. SAVE Effect: บันทึกค่าลง LocalStorage (แยก Key ตาม Org ID)
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    // 🚨 บล็อกไม่ให้ Save ทับถ้าโหลดข้อมูล Org ปัจจุบันยังไม่เสร็จ!
    if (loadedOrgRef.current !== selectedOrganization.id) return;

    // 🎯 ตรวจสอบว่า "ของครบถ้วน" หรือไม่
    const isComplete =
      watchedValues.organizationType &&
      (watchedValues.organizationType === "organization" ||
        (watchedValues.subOrganizations &&
          watchedValues.subOrganizations.length > 0)) &&
      watchedValues.indicator &&
      watchedValues.period &&
      watchedValues.period !== "add"; // ไม่บันทึกจังหวะที่กดปุ่ม "เพิ่มข้อมูล"

    // ✅ ถ้าข้อมูลครบ ถึงจะยอมบันทึกลง LocalStorage
    if (isComplete) {
      const orgKey = getStorageKey(`${selectedOrganization.id}`);
      const filterToSave = {
        organizationType: watchedValues.organizationType,
        subOrganizations: watchedValues.subOrganizations,
        indicator: watchedValues.indicator,
        period: watchedValues.period,
      };
      localStorage.setItem(orgKey, JSON.stringify(filterToSave));
    }
  }, [
    watchedValues.organizationType,
    watchedValues.subOrganizations,
    watchedValues.indicator,
    watchedValues.period,
    selectedOrganization?.id,
  ]);

  // 3. Validation Effect: ตรวจสอบว่าข้อมูลที่โหลดมา (จาก Storage) ยังมีตัวตนจริงไหม
  // ถ้าไม่มี (เช่น ถูกลบไปแล้ว) ให้เคลียร์ออกจาก Form เพื่อป้องกัน Bug
  useEffect(() => {
    // 3.1 Validate SubOrganizations

    const currentOrgType =
      ecoEfficiencyDashboardFilterForm.getValues("organizationType");
    if (
      currentOrgType === "sub-organization" &&
      subOrganizations.length > 0 &&
      selectSubOrganizations &&
      selectSubOrganizations.length > 0
    ) {
      const validSubOrgs = selectSubOrganizations.filter((selectedRefId) =>
        subOrganizations.some((master) => master.refId === selectedRefId),
      );

      if (validSubOrgs.length !== selectSubOrganizations.length) {
        ecoEfficiencyDashboardFilterForm.setValue(
          "subOrganizations",
          validSubOrgs,
        );
      }
    }

    // 3.2 Validate Indicator
    const currentIndicator =
      ecoEfficiencyDashboardFilterForm.getValues("indicator");
    if (indicators.length > 0 && currentIndicator) {
      const exists = indicators.some((i) => i._id === currentIndicator);
      if (!exists) {
        ecoEfficiencyDashboardFilterForm.setValue("indicator", "");
        ecoEfficiencyDashboardFilterForm.setValue("period", "");
      }
    }

    // 3.3 Validate Period (EcoEfficiency)
    const currentPeriod = ecoEfficiencyDashboardFilterForm.getValues("period");
    if (ecoEfficiency.length > 0 && currentPeriod && currentPeriod !== "add") {
      const exists = ecoEfficiency.some((e) => e._id === currentPeriod);
      if (!exists) {
        ecoEfficiencyDashboardFilterForm.setValue("period", "");
        setSelectedEcoEfficiency(undefined);
      }
    }
  }, [subOrganizations, indicators, ecoEfficiency]); // Dependency: Master Data

  const lastFetchedParams = useRef<{ id: string; subOrgs: string }>({
    id: "",
    subOrgs: "",
  });

  useEffect(() => {
    getEcoEfficiency();
    getEconomicIndicators();
  }, []);

  const getEcoEfficiency = async () => {
    try {
      const result: any = await ecoEfficiencyDashboardService.getEcoEfficiency();
      if (result) setEcoEfficiency(result);
    } catch {
      // error alert already shown by service
    }
  };

  const fetchEcoEffData = async (
    indicatorId: string,
    startDate: string,
    endDate: string,
  ) => {
    // Unused
  };

  const buildEcoEffYearlyParams = (
    ecoRefId: string,
    filterForm: UseFormReturn<EcoEfficiencyDashboardFilter>,
  ) => {
    const item = ecoEfficiency.find((e) => e._id === ecoRefId);

    if (!item) throw new Error(`EcoEfficiency with id ${ecoRefId} not found`);

    const organizationType = filterForm.getValues("organizationType");
    // subOrganizations เก็บ refId อยู่แล้ว (storeValue) ไม่ต้องแปลงอีก
    const subOrgRefs =
      organizationType === "organization"
        ? []
        : filterForm.getValues("subOrganizations");

    return {
      indicatorRef: item.economicindicatorsRef._id,
      suborganizationRefs: subOrgRefs,
      startYear: item.year ?? 2023,
      startMonth: item.month ?? 1,
    };
  };

  const ecoEfficiencyMonthlyData = async (
    ecoRefId: string,
    startDate: string,
    endDate: string,
  ) => {
    try {
      const params = buildEcoEffYearlyParams(
        ecoRefId,
        ecoEfficiencyDashboardFilterForm,
      );
      const result: EcoEfficiencyDashboardMonthlyResults | null =
        await ecoEfficiencyDashboardService.getEcoEffDataMonthlySummary(
          params.indicatorRef,
          params.suborganizationRefs,
          params.startYear,
          params.startMonth,
        );
      if (result) setMonthly(result);
      return result;
    } catch (e) {
      console.error(e);
    }
  };

  const ecoEfficiencyYearlyData = async (ecoRefId: string) => {
    try {
      const params = buildEcoEffYearlyParams(
        ecoRefId,
        ecoEfficiencyDashboardFilterForm,
      );
      const result: EcoEfficiencyDashboardYearlyResults | null =
        await ecoEfficiencyDashboardService.getEcoEffDataYearlySummary(
          params.indicatorRef,
          params.suborganizationRefs,
          params.startYear,
          params.startMonth,
        );
      if (result) setYearly(result);
    } catch (e) {
      console.error(e);
    }
  };

  const getEconomicIndicators = async () => {
    try {
      const result = await EcoEfficiencyService.getEconomicIndicators();
      if (result) setIndicators(result);
    } catch {
      // error alert already shown by service
    }
  };

  // Restore Selected Object
  useEffect(() => {
    if (
      ecoEfficiency.length > 0 &&
      selectedPeriodId &&
      (!selectedEcoEfficiency || selectedEcoEfficiency._id !== selectedPeriodId)
    ) {
      const foundItem = ecoEfficiency.find(
        (item) => item._id === selectedPeriodId,
      );
      if (foundItem) setSelectedEcoEfficiency(foundItem);
    }
  }, [ecoEfficiency, selectedPeriodId, selectedEcoEfficiency]);

  // Main Fetch Effect
  useEffect(() => {
    if (!selectedEcoEfficiency) return;

    const currentOrgType =
      ecoEfficiencyDashboardFilterForm.getValues("organizationType");
    const currentSubOrgsName = selectSubOrganizations || [];

    // ป้องกันการยิง API ถ้าเลือก SubOrg แต่ Master List ยังไม่พร้อม
    if (
      currentOrgType === "sub-organization" &&
      currentSubOrgsName.length > 0 &&
      subOrganizations.length === 0
    ) {
      return;
    }

    if (
      currentOrgType === "sub-organization" &&
      currentSubOrgsName.length === 0
    ) {
      setYearly(undefined);
      setMonthly(undefined);
      lastFetchedParams.current = { id: "", subOrgs: "" };
      return;
    }

    // ป้องกัน Double Fetch
    const currentSubOrgKey = JSON.stringify([...currentSubOrgsName].sort());
    const currentId = selectedEcoEfficiency._id;

    if (
      lastFetchedParams.current.id === currentId &&
      lastFetchedParams.current.subOrgs === currentSubOrgKey
    ) {
      return;
    }

    lastFetchedParams.current = {
      id: currentId,
      subOrgs: currentSubOrgKey,
    };

    ecoEfficiencyYearlyData(selectedEcoEfficiency._id);

    if (
      selectedEcoEfficiency &&
      selectedEcoEfficiency.periodType === "monthly"
    ) {
      ecoEfficiencyMonthlyData(
        selectedEcoEfficiency._id,
        selectedEcoEfficiency.startDate,
        selectedEcoEfficiency.endDate,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEcoEfficiency, selectSubOrganizations, subOrganizations]);

  return (
    <Context.Provider
      value={{
        ecoEfficiencyDashboardFilterForm,
        ecoEfficiency,
        fetchEcoEffData,
        selectedEcoEfficiency,
        setSelectedEcoEfficiency,
        fetchedEcoEffData,
        indicators,
        yearly,
        monthly,
        subOrganizations,
        setSubOrganizations,
      }}
    >
      {children}
    </Context.Provider>
  );
};
